/* eslint-disable max-len, func-style */
import { IFieldMetadata } from "Model/Configuration/IFieldMetadata";
import { EntityType } from "Model/EntityType";
import { IItem } from "Model/Storage/IItem";
import { safeUnique } from "./Array";
import { isTrue } from "./Boolean";
import { dateFromString, formatDate, formatTimeRelative } from "./Date";
import {
  isAnyOptionListField,
  isBoolField,
  isDateField,
  isDateTimeField,
  isDecimalField,
  isIntegerField,
  isCurrencyField,
  isStringListField
} from "./Metadata";
import { isNullish } from "./Null";
import { formatCurrency, formatNumber } from "./Number";
import { isNullOrWhiteSpace, joinNonEmpty } from "./String";
import { Translator } from "./Translator";

export function getTranslatedFieldLabel(
  fieldKey: string,
  entityType: EntityType,
  translator?: Translator,
  fallback: string | ((variables?: Record<string, string>) => string) = null
): string {
  if (isNullOrWhiteSpace(fieldKey)) {
    return ``;
  }
  const tr = translator == null ? Translator.getInstance() : translator;
  return tr.get(`storage.${entityType}.${fieldKey}`, () => tr.get(`storage.${fieldKey}`, isNullish(fallback) ? `storage.${entityType}.${fieldKey}` : fallback));
}

export function getTranslatedShortFieldLabel(
  fieldKey: string,
  entityType: EntityType,
  translator?: Translator,
  fallback: string | ((variables?: Record<string, string>) => string) = null
): string {
  if (isNullOrWhiteSpace(fieldKey)) {
    return ``;
  }
  return getTranslatedFieldLabel(
    `${fieldKey}.short`,
    entityType,
    translator,
    isNullish(fallback) ? () => getTranslatedFieldLabel(fieldKey, entityType, translator) : fallback
  );
}

export function getTranslatedSingularFieldLabel(
  fieldKey: string,
  entityType: EntityType,
  translator?: Translator,
  fallback: string | ((variables?: Record<string, string>) => string) = null
): string {
  if (isNullOrWhiteSpace(fieldKey)) {
    return ``;
  }
  return getTranslatedFieldLabel(
    `${fieldKey}.singular`,
    entityType,
    translator,
    isNullish(fallback) ? () => getTranslatedFieldLabel(fieldKey, entityType, translator) : fallback
  );
}

export function getFormattedValue(
  item: IItem,
  fieldMetadata: IFieldMetadata,
  translator: Translator,
  placeholder = `-`
): string {
  const activeLocale = translator.getActiveLocale();
  const { fieldName, entityType, separator } = fieldMetadata;
  const value = item?.[fieldName];

  if (isCurrencyField(fieldMetadata)) {
    const { currencyFieldName, fallbackFieldName } = fieldMetadata;
    if (
      isNullish(value)
      && (isNullOrWhiteSpace(fallbackFieldName) || isNullOrWhiteSpace(item?.[fallbackFieldName] as string))
    ) {
      return placeholder;
    }

    return formatCurrency(activeLocale, value as number, item?.[currencyFieldName] as string);
  }

  if (isNullish(value)) {
    return placeholder;
  }

  if (isIntegerField(fieldMetadata) || isDecimalField(fieldMetadata)) {
    return formatNumber(activeLocale, value as number);
  }

  if (isDateField(fieldMetadata)) {
    if (isNullOrWhiteSpace(value)) {
      return placeholder;
    }

    const date = dateFromString(value);
    return isTrue(fieldMetadata.isRelative, false)
      ? formatTimeRelative(activeLocale, date)
      : formatDate(activeLocale, date, `long`, null);
  }

  if (isDateTimeField(fieldMetadata)) {
    if (isNullOrWhiteSpace(value)) {
      return placeholder;
    }

    const date = dateFromString(value);
    if (date.valueOf() > new Date(8999, 11, 31, 23, 59, 59, 999).valueOf()) {
      return placeholder;
    }

    const isToday = value === `@today@`;
    return isTrue(fieldMetadata.isRelative, false)
      ? formatTimeRelative(activeLocale, date)
      : formatDate(activeLocale, date, `short`, isToday ? null : `short`);
  }

  if (isStringListField(fieldMetadata) && Array.isArray(value)) {
    return joinNonEmpty(value as string[], fieldMetadata.separator ?? `, `);
  }

  if (isAnyOptionListField(fieldMetadata)) {
    return safeUnique(value as string | string[])
      .map((v) => getTranslatedFieldLabel(`${fieldName}.option.${v}`, entityType, translator))
      .join(separator ?? `, `);
  }

  if (isBoolField(fieldMetadata)) {
    return translator.get(`generic.${value?.toString()}`);
  }

  return value?.toString();
}
